import { ErrorMessage, Formik } from 'formik';
import clone from 'lodash/clone';
import isArray from 'lodash/isArray';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import './styles.css';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicCheckbox from '../../../../components/BasicCheckbox';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import DateSelect from '../../../../components/DateSelect';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LoadMask from '../../../../components/LoadMask';
import { ANIMATION_TIMEOUT_APPEAR } from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import ERROR_FIELD from '../../../../constants/error-fields';
import AsyncSelect from '../../../../GbsUI/UI/Select/async';
import useApi from '../../../../hooks/useApi';
import { useTaxonomy } from '../../../../hooks/useTaxonomy';
import { get, post } from '../../../../services/api';
import TextArea from '../EditCandidatePositionForm/components/TextArea';
import { ProfileEditPositionFormValidationSchema } from './validate';

const ProfileEditPositionForm = props => {
  const api = useApi();
  const [init, setInit] = useState(false);
  const [fields, setFields] = useState({});
  const [options, setOptions] = useState({});
  const { data: cities, isLoading: citiesLoading } = useTaxonomy('CITIES');
  const [citiesSearchLoading, setCitiesSearchLoading] = useState(false);
  const [isPresentPosition, setIsPresentPosition] = useState(false);

  const mapCities = useCallback(response => {
    return response.map(group => {
      return {
        label: group.name,
        options: group.options.map(city => ({
          value: city.id,
          label: city.name,
        })),
      };
    });
  }, []);

  const citiesOptions = useMemo(() => {
    return mapCities(cities);
  }, [cities, mapCities]);

  const loadCities = useCallback(
    s => {
      setCitiesSearchLoading(true);

      return new Promise(resolve => {
        void api.get(
          ENDPOINT.TAXONOMIES['CITIES'],
          { s },
          {
            handleError: () => {
              resolve([]);
            },
            handleSuccess: data => {
              resolve(mapCities(data));
            },
            handleComplete: () => {
              setCitiesSearchLoading(false);
            },
          }
        );
      });
    },
    [api, mapCities]
  );

  const onSubmit = useCallback((values, { setSubmitting, setErrors, errors }) => {
    const handleError = response => {
      const obj = {};
      for (let [key, value] of Object.entries(response.data?.fields)) {
        if (key === 'endMonth') {
          key = 'endDate';
        }

        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      setErrors(obj);
    };

    const preparedValues = clone(values);
    preparedValues.id = props.params.id;

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (data === null) {
        delete preparedValues[field];
      } else if (isArray(data)) {
        preparedValues[field] = data.map(item => item.id);
      } else if (typeof data === 'object' && data?.value) {
        preparedValues[field] = data.value;
      } else if (field === 'endDate') {
        preparedValues['endMonth'] = data.month;
        preparedValues['endYear'] = data.year;
        delete preparedValues.endDate;
      }
    });

    void post(ENDPOINT.FORM.SAVE.PROFILE_EXPERIENCE, preparedValues, {
      handleForbidden: handleError,
      handleError,
      handleSuccess: () => props.actions.proceed(values),
      handleComplete: () => setSubmitting(false),
    });
  }, [props.actions, props.params.id]);

  useEffect(() => {
    void get(
      ENDPOINT.FORM.GET.EXPERIENCE,
      { id: props.params.id },
      {
        handleSuccess: formData => {
          setFields({
            description: formData.fields.description || '',
            cityId: formData.fields.cityId || null,
            present: !!formData.fields.present,
            endDate:
              formData.fields.endMonth && formData.fields.endYear
                ? {
                  month: formData.fields.endMonth,
                  year: formData.fields.endYear,
                }
                : null,
          });
          setIsPresentPosition(formData.fields.present);
          setOptions(formData.options);
          setInit(true);
        },
      }
    );
  }, [props.params.id]);

  if (!init) {
    return (
      <div className="form__inner">
        <div className="horizontal-loader__container">
          <HorizontalLoader />
        </div>
      </div>
    );
  }

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={fields}
          validationSchema={ProfileEditPositionFormValidationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (
              <form onSubmit={onSubmit} className="form__content">
                {isSubmitting && <LoadMask/>}

                {isPresentPosition && (
                  <>
                    {!values.present && (
                      <div className="form__group form__group--compact">
                        <DateSelect
                          required
                          isLast={true}
                          label="End Date"
                          name="endDate"
                          controlFunc={setFieldValue}
                          touchFunc={setFieldTouched}
                          values={values.endDate || {}}
                          yearOptions={options?.Date?.year}
                          isValid={
                            (!errors.endDate && !!touched.endDate) ||
                            !touched.endDate
                          }
                        />

                        <ErrorMessage
                          name="endDate"
                          render={message => (
                            <div className="form__error">{message}</div>
                          )}
                        />
                        <ErrorMessage
                          name="endMonth"
                          render={message => (
                            <div className="form__error">{message}</div>
                          )}
                        />
                      </div>
                    )}

                    <div className="form__group form__group--compact">
                      <div className="form__row">
                        <BasicCheckbox
                          value={values.present}
                          singleRow
                          checked={values.present}
                          title="Present"
                          name="present"
                          controlFunc={handleChange}
                          labelStyles={{
                            fontFamily: 'Noto Sans, sans-serif',
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="form__group form__group--compact">
                  <AsyncSelect
                    label={
                      <span
                        className={
                          'lazy-load-select__label-text lazy-load-select__label-text--required'
                        }
                      >
                      CITY
                    </span>
                    }
                    defaultOptions={citiesOptions}
                    isLoading={citiesLoading || citiesSearchLoading}
                    loadOptions={loadCities}
                    value={values.cityId}
                    onChange={v => {
                      setFieldValue('cityId', v);
                    }}
                    onBlur={() => {
                      setFieldTouched('cityId', true);
                    }}
                    isSearchable
                  />

                  <ErrorMessage
                    name="cityId"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group form__group--compact">
                  <TextArea
                    name="description"
                    value={values.description || ''}
                    labelText="Description"
                    placeholder="0 characters of the 2000 character limit"
                    isValid={
                      (!errors.description && !!touched.description) ||
                      !touched.description
                    }
                    required={false}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={10}
                    resize={false}
                  />

                  <ErrorMessage
                    name="description"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__controls form__controls--compact">
                  <BasicButtonGroup>
                    <BasicSubmitButton
                      controlFunc={handleSubmit}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={true}
                      btnTextColor="#27282d"
                      btnText="Save"
                    />

                    <BasicButton
                      controlFunc={props.actions.dismiss}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={false}
                      btnTextColor="#27282d"
                      btnText="Cancel"
                    />
                  </BasicButtonGroup>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </CSSTransition>
  );
};

export default ProfileEditPositionForm;
